import React, { useState, useEffect, useCallback } from "react";
import SelectContributorIcon from "../components/SelectContributorIcon";
import VenmoUsernameSection from "../components/VenmoUsernameSection";
import ZellePaymentSection from "../components/ZellePaymentSection";
import { postContributor } from "../services/AnimalSplitCore";
import { toggleModal } from "../utils/utils";
import { getCookie, setCookie } from "../utils/cookies";
import { isVenmoUsernameCapitalizationValid } from "../utils/venmo";
import { MODAL_ID } from "../constants";
import { useTranslation } from 'react-i18next';
import AnimalEasterEggBadge from "../components/AnimalEasterEggBadge";
import "./ModalTheme.css";

const VENMO_USERNAME_COOKIE = "as:venmoUsername";
const ZELLE_USERNAME_COOKIE = "as:zelleUsername";
const ZELLE_RECIPIENT_COOKIE = "as:zelleRecipientName";

export default function CreateContributor(props) {
  const { t } = useTranslation();
  const { modalId, groupId, renderGroup, setIsLoading } = props;

  const [contributorName, setContributorName] = useState("");
  const [contributorIconId, setContributorIconId] = useState("");
  const [showVenmoSection, setShowVenmoSection] = useState(false);
  const [venmoUsername, setVenmoUsername] = useState("");
  const [venmoResetSignal, setVenmoResetSignal] = useState(0);
  const [showZelleSection, setShowZelleSection] = useState(false);
  const [zelleUsername, setZelleUsername] = useState("");
  const [zelleRecipientName, setZelleRecipientName] = useState("");
  const [formValidated, setFormValidated] = useState(false);
  const [isVenmoValid, setIsVenmoValid] = useState(true);
  const [isZelleValid, setIsZelleValid] = useState(true);

  const handleModalWillShow = useCallback(() => {
    const remembered = getCookie(VENMO_USERNAME_COOKIE) || "";
    const rememberedZelleUser = getCookie(ZELLE_USERNAME_COOKIE) || "";
    const rememberedZelleName = getCookie(ZELLE_RECIPIENT_COOKIE) || "";
    setContributorName("");
    setContributorIconId("");
    setShowVenmoSection(false);
    setVenmoUsername(String(remembered || ""));
    setVenmoResetSignal((v) => v + 1);
    setShowZelleSection(false);
    setZelleUsername(String(rememberedZelleUser || ""));
    setZelleRecipientName(String(rememberedZelleName || ""));
    setFormValidated(false);
    setIsVenmoValid(true); // Reset to valid state when opening modal
    setIsZelleValid(true);
  }, [
    setContributorName,
    setContributorIconId,
    setShowVenmoSection,
    setVenmoUsername,
    setVenmoResetSignal,
    setFormValidated,
  ]);

  useEffect(() => {
    const editContributorCreationModal = document.getElementById(modalId);
    if (!editContributorCreationModal) return;
    editContributorCreationModal.addEventListener(
      "show.bs.modal",
      handleModalWillShow
    );

    return () => {
      editContributorCreationModal.removeEventListener(
        "show.bs.modal",
        handleModalWillShow
      );
    };
  }, [modalId, handleModalWillShow]);

  const handleCreateContributor = async () => {
    if (!isFormValid()) {
      setFormValidated(true);
      return;
    }

    try {
      setIsLoading?.(true);
      const normalizedVenmoUsername = venmoUsername.trim().replace(/^@/, "");
      // Hard-block invalid capitalization on save (avoid stale validation state)
      if (
        showVenmoSection &&
        normalizedVenmoUsername &&
        !isVenmoUsernameCapitalizationValid(normalizedVenmoUsername)
      ) {
        setIsLoading?.(false);
        setFormValidated(true);
        return;
      }
      const normalizedZelleUsername = zelleUsername.trim();
      const normalizedZelleRecipientName = zelleRecipientName.trim();
      const { payload } = await postContributor(groupId, {
        name: contributorName,
        icon: contributorIconId,
        venmoUsername: showVenmoSection ? normalizedVenmoUsername || null : null,
        zelleUsername: showZelleSection ? normalizedZelleUsername || null : null,
        zelleRecipientName: showZelleSection ? normalizedZelleRecipientName || null : null,
      });
      setIsLoading?.(false);
      renderGroup(payload);
      if (showVenmoSection && normalizedVenmoUsername) {
        setCookie(VENMO_USERNAME_COOKIE, normalizedVenmoUsername);
      }
      if (showZelleSection && normalizedZelleUsername && normalizedZelleRecipientName) {
        setCookie(ZELLE_USERNAME_COOKIE, normalizedZelleUsername);
        setCookie(ZELLE_RECIPIENT_COOKIE, normalizedZelleRecipientName);
      }
    } catch (error) {
      setIsLoading?.(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };

  const isFormValid = () => {
    if (contributorName === "") return false;
    // If Venmo section is enabled, validate the username
    if (showVenmoSection && !isVenmoValid) return false;
    if (showZelleSection && !isZelleValid) return false;
    return true;
  };

  return (
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content as-modal as-theme">
          <div className="modal-header">
            <div className="as-modal-header-row">
              <div className="as-modal-title-row">
                <span className="as-modal-icon-badge" aria-hidden="true">
                  🐾
                </span>
                <h5 className="modal-title" id="exampleModalLabel">
                  {t('participant.create_participant')}
                </h5>
              </div>
              <div className="as-modal-actions">
                <AnimalEasterEggBadge seed={`${groupId}:create-contributor`} />
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                ></button>
              </div>
            </div>
          </div>

          <div className={`modal-body ${formValidated ? "was-validated" : ""}`}>
            <label className="form-label as-field-label">
              <span className="as-field-label-badge" aria-hidden="true">🐾</span>
              <span className="as-field-label-text">{t('participant.participant_name')}</span>
            </label>
            <div className="d-flex">
              <input
                type="text"
                className="form-control me-2 as-name-input"
                id="participantName"
                value={contributorName}
                onChange={(e) => setContributorName(e.target.value)}
                required
              />
              <SelectContributorIcon
                setContributorIconId={setContributorIconId}
                contributorIconId={contributorIconId}
              />
            </div>

            <VenmoUsernameSection
              idBase={`${modalId}-venmo`}
              modalId={modalId}
              enabled={showVenmoSection}
              onEnabledChange={setShowVenmoSection}
              venmoUsername={venmoUsername}
              setVenmoUsername={setVenmoUsername}
              resetSignal={venmoResetSignal}
              onValidationChange={setIsVenmoValid}
            />

            <ZellePaymentSection
              idBase={`${modalId}-zelle`}
              enabled={showZelleSection}
              onEnabledChange={setShowZelleSection}
              zelleUsername={zelleUsername}
              setZelleUsername={setZelleUsername}
              zelleRecipientName={zelleRecipientName}
              setZelleRecipientName={setZelleRecipientName}
              onValidationChange={setIsZelleValid}
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn as-btn-soft"
              data-bs-dismiss="modal"
            >
              {t('general.close')}
            </button>
            <button
              type="button"
              className="btn btn-primary as-btn-primary-gradient"
              data-bs-dismiss={`${isFormValid() ? "modal" : ""}`}
              onClick={handleCreateContributor}
              disabled={!isFormValid()}
            >
              {t('general.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
